import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar'; // Custom hook for Snackbar
import { TechnicianFilterData } from '../../../pages/Technicians/Technicians';

const TechnicianFilter = ({
  onFiltered,
  onResetFilter,
  handleDrawerClose,
  filters,
  showType = true,
  showPosition = true,
  showStatus = true,
  showKycStatus = true,
}: any) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar(); // Using custom hook for Snackbar
  const [filterData, setFilterData] = useState<TechnicianFilterData>({
    type: filters?.type,
    position: filters?.position,
    status: filters?.status,
    kycStatus: filters?.kycStatus,
  });

  useEffect(() => {
    setFilterData({
      type: filters?.type || '',
      position: filters?.position || '',
      status: filters?.status || '',
      kycStatus: filters?.kycStatus || '',
    });
  }, [filters]);

  // Handle dropdown changes
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name as string]: value as string,
    }));
  };

  // Handle form submit (applying the filter)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const hasSelectedFilter =
      (showType && Boolean(filterData.type)) ||
      (showPosition && Boolean(filterData.position)) ||
      (showStatus && Boolean(filterData.status)) ||
      (showKycStatus && Boolean(filterData.kycStatus));

    if (!hasSelectedFilter) {
      showSnackbar('Please select at least one filter', 'warning');
      return;
    }

    onFiltered(filterData);
    handleDrawerClose();
  };

  // Handle form reset
  const handleReset = () => {
    setFilterData((prev) => ({
      ...prev,
      type: showType ? '' : prev.type,
      position: showPosition ? '' : prev.position,
      status: showStatus ? '' : prev.status,
      kycStatus: showKycStatus ? '' : prev.kycStatus,
    }));
    onResetFilter();
  };

  return (
    <Box sx={{ flex: 1 }}>
      {SnackbarComponent}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Type Filter */}
        {showType && (
          <FormControl fullWidth variant="outlined">
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={filterData.type}
              onChange={handleChange}
              label="Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACD">ACD</MenuItem>
              <MenuItem value="FC">FC</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Position Filter */}
        {showPosition && (
          <FormControl fullWidth variant="outlined">
            <InputLabel>Position</InputLabel>
            <Select
              name="position"
              value={filterData.position}
              onChange={handleChange}
              label="Position"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="HELPER">Helper</MenuItem>
              <MenuItem value="TECHNICIAN">Technician</MenuItem>
              <MenuItem value="SENIOR TECHNICIAN">Senior Technician</MenuItem>
              <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
              <MenuItem value="MANAGER">Manager</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Status Filter */}
        {showStatus && (
          <FormControl fullWidth variant="outlined">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={filterData.status}
              onChange={handleChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              {/* <MenuItem value="KYC_PENDING">KYC Pending</MenuItem> */}
              <MenuItem value="ON_JOB">On Job</MenuItem>
              <MenuItem value="AVAILABLE">Available</MenuItem>
              <MenuItem value="ON_LEAVE">On Leave</MenuItem>
              <MenuItem value="ON_BREAK">On Break</MenuItem>
              <MenuItem value="ON_TRAINING">On Training</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* KYC Status Filter */}
        {showKycStatus && (
          <FormControl fullWidth variant="outlined">
            <InputLabel>KYC Status</InputLabel>
            <Select
              name="kycStatus"
              value={filterData.kycStatus}
              onChange={handleChange}
              label="KYC Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="VERIFIED">Verified</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="REQUESTED">Requested</MenuItem>
              <MenuItem value="REVIEW_REQUESTED">Review Requested</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button type="button" variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Apply Filter
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export { TechnicianFilter };
